/**
 * 文件定位：
 * 1. 当前文件位于 Worker 倒角求解纯函数层；
 * 2. 负责 Puppy CAD 内核 临时对象生命周期、进度对象和结果校验；
 * 3. 不放 class，避免与 solver 封装目录混合职责。
 *
 * 文件职责：
 * 1. 统一释放 Puppy CAD 内核 临时对象；
 * 2. 统一构建 ProgressRange；
 * 3. 统一把 Puppy CAD 内核 builder 结果转换为明确错误或可用 Shape。
 */

import type { ChamferKernelInstance } from '../types/ChamferShapeBuildInput';
import { assertFilletLikeBuilderDiagnosticsHealthy } from '../../fillet-builder';

/**
 * 安全释放 Puppy CAD 内核 临时对象。
 *
 * @param value 待释放对象。
 */
export function safeDelete(value: unknown): void {
  const deleteMethod = (value as { delete?: () => void } | null)?.delete;
  if (typeof deleteMethod !== 'function') {
    return;
  }
  try {
    deleteMethod.call(value);
  } catch {
    /**
     * 释放失败只说明绑定层对象已不可用。
     * 这里不能覆盖主求解错误，否则用户会看到无关的生命周期噪声。
     */
  }
}

/**
 * 构建 Puppy CAD 内核 进度范围对象。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @returns 进度范围对象；当前绑定缺失时返回 undefined。
 */
export function buildProgressRange(puppy: ChamferKernelInstance): unknown {
  const ctor = (puppy as Record<string, unknown>).Message_ProgressRange_1 as
    | (new () => unknown)
    | undefined;
  return typeof ctor === 'function' ? new ctor() : undefined;
}

/**
 * 把角度制转换为弧度制。
 *
 * @param angleDeg 角度值。
 * @returns 弧度值。
 */
export function toRadians(angleDeg: number): number {
  return angleDeg * (Math.PI / 180);
}

/**
 * 校验倒角 builder 是否产生可用 Shape。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param builder Puppy CAD 内核 倒角 builder。
 * @returns 求解后的 Shape。
 */
export function assertChamferBuilderResultSafe(
  puppy: ChamferKernelInstance,
  builder: Record<string, unknown>,
): unknown {
  const isDone = builder.IsDone as (() => boolean) | undefined;
  if (typeof isDone === 'function' && !isDone.call(builder)) {
    throw new Error('倒角求解失败，Puppy CAD 内核 未完成构建');
  }

  const shapeGetter = builder.Shape as (() => unknown) | undefined;
  let resultShape: unknown = null;
  let shapeError: Error | null = null;
  try {
    resultShape = typeof shapeGetter === 'function' ? shapeGetter.call(builder) : null;
  } catch (error) {
    shapeError = error instanceof Error ? error : new Error(String(error));
  }

  if (!resultShape) {
    const hasResult = builder.HasResult as (() => boolean) | undefined;
    if (typeof hasResult === 'function' && !hasResult.call(builder)) {
      throw new Error(shapeError?.message ?? '倒角求解失败，Puppy CAD 内核 未返回可用结果');
    }
    throw new Error(shapeError?.message ?? '倒角求解失败，未返回 Shape');
  }

  /**
   * MakeChamfer 与 MakeFillet 共享同类 builder 诊断接口。
   * 超大距离/角度时也可能出现 IsDone=true 但 StripeStatus 已失败的部分结果。
   */
  try {
    assertFilletLikeBuilderDiagnosticsHealthy(puppy as never, builder);
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    throw new Error(`倒角求解失败，${reason}`);
  }

  return resultShape;
}

/**
 * 使用 Puppy CAD 内核 8 的三参数构造签名执行布尔切除。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param targetShape 被切除实体。
 * @param toolShape 切除工具体。
 * @returns 切除后的 Shape。
 */
export function buildCutShape(
  puppy: ChamferKernelInstance,
  targetShape: unknown,
  toolShape: unknown,
): unknown {
  const kernelRecord = puppy as Record<string, unknown>;
  const progress = buildProgressRange(puppy);
  const cutCtor = kernelRecord.BRepAlgoAPI_Cut_3 as
    | (new (left: unknown, right: unknown, progressRange: unknown) => Record<string, unknown>)
    | undefined;
  if (typeof cutCtor !== 'function') {
    safeDelete(progress);
    throw new Error('当前 Puppy CAD 内核 绑定缺少 BRepAlgoAPI_Cut_3 构造器');
  }

  let builder: Record<string, unknown> | null = null;
  try {
    builder = new cutCtor(targetShape, toolShape, progress);
    if (typeof builder.SetFuzzyValue === 'function') {
      builder.SetFuzzyValue(1e-7);
    }
    if (typeof builder.Build === 'function') {
      builder.Build(progress);
    }
    const shapeGetter = builder.Shape as (() => unknown) | undefined;
    if (typeof shapeGetter !== 'function') {
      throw new Error('布尔切除未返回 Shape');
    }
    return shapeGetter.call(builder);
  } finally {
    safeDelete(builder);
    safeDelete(progress);
  }
}

